import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { trackProductEvent } from "@/lib/productMetrics";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import GlobalSongMatchPanel from "@/components/GlobalSongMatchPanel";
import ChordLearningPanel from "@/components/music/ChordLearningPanel";
import StemQuickMixer from "@/components/stems/StemQuickMixer";
import useStemTask from "@/components/stems/useStemTask";
import { stemsAreReady, stemStatusLabel } from "@/components/stems/stemTask";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { getChordNotes } from "@/music/chords/chordParser";
import {
  readLearningInstrumentPreference,
  writeLearningInstrumentPreference,
} from "@/music/chords/learningInstrumentPreference";
import {
  buildChordSyncState,
  buildTimelineStarts,
  findActiveTimelineIndexAtTime,
  normalizeChordTimeline,
  normalizeSectionTimeline,
  toFiniteNumber,
} from "@/music/chords/chordSyncUtils";
import {
  ArrowLeft,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Repeat,
  Volume2,
  VolumeX,
  Loader2,
  MessageSquare,
  Crown,
  FileAudio,
  FileText,
  SlidersHorizontal,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

type ChordInfo = {
  time?: number;
  start?: number;
  timestamp?: number;
  start_time?: number;
  end?: number;
  end_time?: number;
  chord: string;
  duration?: number;
  name?: string;
  label?: string;
  chord_name?: string;
  symbol?: string;
};

type StructureSection = {
  name: string;
  start: number;
  end: number;
};

type AnalysisData = {
  id: string;
  filename: string;
  audio_url?: string;
  source_audio_url?: string;
  playback_url?: string;
  preview_audio_url?: string;
  audio_mime_type?: string;
  audio_file_ext?: string;
  playback_format?: string;
  key: string;
  bpm: number;
  time_signature: string;
  chords: ChordInfo[];
  structure: StructureSection[];
  duration: number;
  created_at: string;
};

const STEM_KEYS = ["vocals", "drums", "bass", "other"] as const;
type StemKey = (typeof STEM_KEYS)[number];

const getChordType = (chord: string) => {
  if (chord.includes("dim")) return "diminished";
  if (chord.includes("7") || chord.includes("maj7")) return "seventh";
  if (chord.includes("m") && !chord.includes("maj")) return "minor";
  if (chord.match(/^[A-G]#?b?$/)) return "major";
  return "default";
};

const CHORD_COLORS: Record<string, string> = {
  major: "bg-primary/20 border-primary/40 text-primary",
  minor: "bg-secondary/20 border-secondary/40 text-secondary",
  seventh: "bg-accent/20 border-accent/40 text-accent",
  diminished: "bg-destructive/20 border-destructive/40 text-destructive",
  default: "bg-white/10 border-white/20 text-white",
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const UI_SYNC_INTERVAL_MS = 100;
const TIME_EPSILON = 0.02;
const CHORD_LEARNING_SYNC_INTERVAL_MS = 150;
const EMPTY_CHORD_SYNC = {
  activeIndex: -1,
  activeChord: null as ChordInfo | null,
  nextIndex: -1,
  nextChord: null as ChordInfo | null,
  timeToNextChord: null as number | null,
};

type ChordsStripProps = {
  chords: ChordInfo[];
  currentChordIndex: number;
  isPro: boolean;
  onJump: (index: number) => void;
};

type ChordButtonProps = {
  chord: ChordInfo;
  index: number;
  isCurrent: boolean;
  isPro: boolean;
  onJump: (index: number) => void;
};

const ChordButton = memo(({ chord, index, isCurrent, isPro, onJump }: ChordButtonProps) => (
  <button
    onClick={() => (isPro ? onJump(index) : undefined)}
    className={`flex-shrink-0 px-4 py-2 rounded-lg border transition-all ${
      isCurrent
        ? `${CHORD_COLORS[getChordType(chord.chord)]} ring-1 ring-white`
        : "bg-black/30 border-white/10 text-gray-300"
    } ${isPro ? "cursor-pointer" : "cursor-default"}`}
  >
    <div className="text-sm font-semibold">{chord.chord}</div>
    <div className="text-xs opacity-70">{formatTime(chord.time || 0)}</div>
  </button>
));

ChordButton.displayName = "ChordButton";

const ChordsStrip = memo(({ chords, currentChordIndex, isPro, onJump }: ChordsStripProps) => (
  <div className="flex gap-2 overflow-x-auto pb-2">
    {chords.map((chord, index) => (
      <ChordButton
        key={`${chord.chord}-${index}`}
        chord={chord}
        index={index}
        isCurrent={index === currentChordIndex}
        isPro={isPro}
        onJump={onJump}
      />
    ))}
  </div>
));

ChordsStrip.displayName = "ChordsStrip";

const AnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const isPro = (user?.plan || "free") === "pro";

  const mainAudioRef = useRef<HTMLAudioElement>(null);
  const stemRefs = useRef<Record<StemKey, HTMLAudioElement | null>>({
    vocals: null,
    drums: null,
    bass: null,
    other: null,
  });
  const progressRef = useRef<HTMLDivElement>(null);
  const timelineDraggingRef = useRef(false);
  const playbackTimeRef = useRef(0);
  const chordIndexRef = useRef(-1);
  const sectionIndexRef = useRef(-1);
  const lastUiSyncRef = useRef(0);
  const lastChordLearningSyncRef = useRef(0);
  const practiceModeTrackedRef = useRef(false);
  const getAllActiveAudiosRef = useRef<() => HTMLAudioElement[]>(() => []);
  const syncAudioSettingsRef = useRef<() => void>(() => {});
  const seekToRef = useRef<(time: number) => void>(() => {});

  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("practice");

  const { task: stemsTask, retry: retryStems, retrying: retryingStems, error: stemsStatusError } = useStemTask(id, getAuthHeaders);
  const quickMixerRef = useRef<{ pause: () => void } | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);

  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopSectionIndex, setLoopSectionIndex] = useState<number | null>(null);

  const [playbackRate, setPlaybackRate] = useState(1);
  const [useStemPractice, setUseStemPractice] = useState(false);
  const [stemMuted, setStemMuted] = useState<Record<StemKey, boolean>>({
    vocals: false,
    drums: false,
    bass: false,
    other: false,
  });

  const [currentChordIndex, setCurrentChordIndex] = useState(-1);
  const [currentSectionName, setCurrentSectionName] = useState<string | null>(null);
  const [chordSync, setChordSync] = useState(EMPTY_CHORD_SYNC);
  const [learningInstrument, setLearningInstrument] = useState(() => readLearningInstrumentPreference());

  const playbackAudioUrl = analysis?.playback_url || analysis?.preview_audio_url || analysis?.audio_url || "";
  const hasAudio = !!playbackAudioUrl;
  const stemsReady = stemsAreReady(stemsTask);
  const canUseProPractice = isPro && stemsReady;
  const analysisDuration = duration || toFiniteNumber(analysis?.duration, 0) || 0;
  const chords = useMemo(
    () => normalizeChordTimeline(analysis?.chords ?? [], analysisDuration),
    [analysis?.chords, analysisDuration]
  );
  const sections = useMemo(
    () => normalizeSectionTimeline(analysis?.structure ?? [], analysisDuration),
    [analysis?.structure, analysisDuration]
  );
  const chordStarts = useMemo(() => buildTimelineStarts(chords), [chords]);
  const sectionStarts = useMemo(() => buildTimelineStarts(sections), [sections]);

  const getPracticeTimelineAudio = useCallback((): HTMLAudioElement | null => {
    if (useStemPractice && canUseProPractice) {
      return stemRefs.current.vocals || stemRefs.current.drums || stemRefs.current.bass || stemRefs.current.other;
    }
    return mainAudioRef.current;
  }, [useStemPractice, canUseProPractice]);

  const getAllActiveAudios = useCallback((): HTMLAudioElement[] => {
    if (useStemPractice && canUseProPractice) {
      return STEM_KEYS.map((key) => stemRefs.current[key]).filter(Boolean) as HTMLAudioElement[];
    }
    return mainAudioRef.current ? [mainAudioRef.current] : [];
  }, [useStemPractice, canUseProPractice]);

  const syncTimelineAtTime = useCallback(
    (time: number, { forceUi = false } = {}) => {
      const safeTime = Math.max(0, Math.min(Number(time) || 0, analysisDuration || 0));
      playbackTimeRef.current = safeTime;

      const nextSync = buildChordSyncState(chords, chordStarts, safeTime, analysisDuration, TIME_EPSILON);
      const chordChanged = nextSync.activeIndex !== chordIndexRef.current;
      const now = performance.now();
      const shouldUpdateChordUi =
        forceUi ||
        chordChanged ||
        now - lastChordLearningSyncRef.current >= CHORD_LEARNING_SYNC_INTERVAL_MS;

      chordIndexRef.current = nextSync.activeIndex;

      if (shouldUpdateChordUi) {
        lastChordLearningSyncRef.current = now;
        setChordSync((prev) => {
          if (
            prev.activeIndex === nextSync.activeIndex &&
            prev.nextIndex === nextSync.nextIndex &&
            prev.activeChord === nextSync.activeChord &&
            prev.nextChord === nextSync.nextChord &&
            prev.timeToNextChord === nextSync.timeToNextChord
          ) {
            return prev;
          }
          return nextSync;
        });
      }

      if (forceUi || chordChanged) {
        setCurrentChordIndex((prev) => (prev === nextSync.activeIndex ? prev : nextSync.activeIndex));
      }

      const sectionIndex = findActiveTimelineIndexAtTime(sections, sectionStarts, safeTime, 0);
      if (forceUi || sectionIndex !== sectionIndexRef.current) {
        sectionIndexRef.current = sectionIndex;
        setCurrentSectionName(sectionIndex >= 0 ? sections[sectionIndex].name : null);
      }

      if (forceUi) {
        lastUiSyncRef.current = performance.now();
        setCurrentTime((prev) => (Math.abs(prev - safeTime) > TIME_EPSILON ? safeTime : prev));
      }
    },
    [analysisDuration, chordStarts, chords, sectionStarts, sections]
  );

  const handleLearningInstrumentChange = useCallback((instrument: string) => {
    setLearningInstrument(writeLearningInstrumentPreference(instrument));
  }, []);

  const seekTo = useCallback(
    (time: number) => {
      const safeTime = Math.max(0, Math.min(Number(time) || 0, duration || analysisDuration || 0));
      getAllActiveAudios().forEach((audio) => {
        audio.currentTime = safeTime;
      });
      syncTimelineAtTime(safeTime, { forceUi: true });
    },
    [analysisDuration, duration, getAllActiveAudios, syncTimelineAtTime]
  );

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/analysis/${id}`, {
        headers: getAuthHeaders(),
      });
      setAnalysis(response.data);
      setDuration(response.data.duration);
      playbackTimeRef.current = 0;
      chordIndexRef.current = -1;
      sectionIndexRef.current = -1;
      lastUiSyncRef.current = 0;
      lastChordLearningSyncRef.current = 0;
      setCurrentChordIndex(-1);
      setCurrentSectionName(null);
      setChordSync(EMPTY_CHORD_SYNC);
    } catch (error) {
      toast.error("Error al cargar el análisis");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const syncAudioSettings = useCallback(() => {
    const baseVolume = isMuted ? 0 : volume / 100;
    const rate = isPro ? playbackRate : 1;

    if (useStemPractice && canUseProPractice) {
      STEM_KEYS.forEach((key) => {
        const audio = stemRefs.current[key];
        if (!audio) return;
        audio.playbackRate = rate;
        audio.volume = stemMuted[key] ? 0 : baseVolume;
      });
    } else if (mainAudioRef.current) {
      mainAudioRef.current.playbackRate = rate;
      mainAudioRef.current.volume = baseVolume;
    }
  }, [isMuted, volume, isPro, playbackRate, useStemPractice, canUseProPractice, stemMuted]);

  useEffect(() => {
    syncAudioSettings();
  }, [syncAudioSettings]);

  useEffect(() => {
    getAllActiveAudiosRef.current = getAllActiveAudios;
  }, [getAllActiveAudios]);

  useEffect(() => {
    syncAudioSettingsRef.current = syncAudioSettings;
  }, [syncAudioSettings]);

  const togglePlay = useCallback(() => {
    quickMixerRef.current?.pause();
    const audios = getAllActiveAudios();
    if (!audios.length) return;

    if (isPlaying) {
      const pausedTime = playbackTimeRef.current;
      audios.forEach((audio) => audio.pause());
      syncTimelineAtTime(pausedTime, { forceUi: true });
      setIsPlaying(false);
      return;
    }

    Promise.all(
      audios.map(async (audio) => {
        try {
          audio.currentTime = playbackTimeRef.current;
          await audio.play();
        } catch (error) {
          console.error("Playback failed", error);
        }
      })
    ).then(() => {
      syncTimelineAtTime(playbackTimeRef.current, { forceUi: true });
      setIsPlaying(true);
      if (!practiceModeTrackedRef.current) {
        practiceModeTrackedRef.current = true;
        trackProductEvent(
          "practice_mode_started",
          { analysis_id: id, using_stems: useStemPractice && canUseProPractice },
          getAuthHeaders
        );
      }
    });
  }, [canUseProPractice, getAllActiveAudios, getAuthHeaders, id, isPlaying, syncTimelineAtTime, useStemPractice]);

  const skipBack = () => seekTo(playbackTimeRef.current - 10);
  const skipForward = () => seekTo(playbackTimeRef.current + 10);
  const restart = () => seekTo(0);

  useEffect(() => {
    seekToRef.current = seekTo;
  }, [seekTo]);

  const seekFromProgressPointer = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const timelineAudio = getPracticeTimelineAudio();
      if (!progressRef.current || !timelineAudio || !duration) return;
      const rect = progressRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      seekTo(percent * duration);
    },
    [duration, getPracticeTimelineAudio, seekTo]
  );

  const handleProgressPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      timelineDraggingRef.current = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      seekFromProgressPointer(event);
    },
    [seekFromProgressPointer]
  );

  const handleProgressPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!timelineDraggingRef.current) return;
      seekFromProgressPointer(event);
    },
    [seekFromProgressPointer]
  );

  const handleProgressPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      timelineDraggingRef.current = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      seekFromProgressPointer(event);
    },
    [seekFromProgressPointer]
  );

  const handleProgressPointerCancel = useCallback(() => {
    timelineDraggingRef.current = false;
  }, []);

  const handleProgressKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        seekTo(playbackTimeRef.current - 5);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        seekTo(playbackTimeRef.current + 5);
      }
      if (event.key === "Home") {
        event.preventDefault();
        seekTo(0);
      }
      if (event.key === "End") {
        event.preventDefault();
        seekTo(duration || 0);
      }
    },
    [duration, seekTo]
  );

  const loopSection = useMemo(
    () => (loopSectionIndex !== null ? sections[loopSectionIndex] ?? null : null),
    [loopSectionIndex, sections]
  );

  useEffect(() => {
    const timelineAudio = getPracticeTimelineAudio();
    if (!timelineAudio) return;

    const handleTimeUpdate = () => {
      const t = timelineAudio.currentTime;
      syncTimelineAtTime(t);

      if (analysis) {
        if (loopEnabled && loopSection && t >= loopSection.end) {
          seekTo(loopSection.start);
          return;
        }
      }

      const now = performance.now();
      if (now - lastUiSyncRef.current >= UI_SYNC_INTERVAL_MS) {
        lastUiSyncRef.current = now;
        setCurrentTime((prev) => (Math.abs(prev - t) > TIME_EPSILON ? t : prev));
      }
    };

    const handleLoadedMetadata = () => {
      const nextDuration = timelineAudio.duration || analysis?.duration || 0;
      setDuration((prev) => (Math.abs(prev - nextDuration) > TIME_EPSILON ? nextDuration : prev));
      syncTimelineAtTime(timelineAudio.currentTime || playbackTimeRef.current || 0, { forceUi: true });
    };
    const handlePlay = () => {
      setIsPlaying(true);
      syncTimelineAtTime(timelineAudio.currentTime || playbackTimeRef.current || 0, { forceUi: true });
    };
    const handlePause = () => {
      setIsPlaying(false);
      const pausedTime = timelineAudio.currentTime || 0;
      syncTimelineAtTime(pausedTime, { forceUi: true });
    };

    timelineAudio.addEventListener("timeupdate", handleTimeUpdate);
    timelineAudio.addEventListener("loadedmetadata", handleLoadedMetadata);
    timelineAudio.addEventListener("play", handlePlay);
    timelineAudio.addEventListener("pause", handlePause);

    return () => {
      timelineAudio.removeEventListener("timeupdate", handleTimeUpdate);
      timelineAudio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      timelineAudio.removeEventListener("play", handlePlay);
      timelineAudio.removeEventListener("pause", handlePause);
    };
  }, [
    analysis,
    getPracticeTimelineAudio,
    loopEnabled,
    loopSection,
    seekTo,
    syncTimelineAtTime,
  ]);

  useEffect(() => {
    const audiosBeforeSwitch = getAllActiveAudiosRef.current();
    const wasPlaying = audiosBeforeSwitch.some((audio) => !audio.paused);
    const targetTime = playbackTimeRef.current;

    audiosBeforeSwitch.forEach((audio) => audio.pause());
    syncAudioSettingsRef.current();
    seekToRef.current(targetTime);

    if (wasPlaying) {
      getAllActiveAudiosRef.current().forEach((audio) => audio.play().catch(() => null));
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [useStemPractice]);

  useEffect(() => {
    if (!analysis) return;
    syncTimelineAtTime(playbackTimeRef.current || 0, { forceUi: true });
  }, [analysis, chords, sections, syncTimelineAtTime]);

  useEffect(() => {
    if (!analysis || !isPlaying) return undefined;
    let frameId = 0;

    const tick = () => {
      const timelineAudio = getPracticeTimelineAudio();
      if (timelineAudio && !timelineAudio.paused) {
        const t = timelineAudio.currentTime || 0;
        syncTimelineAtTime(t);
        const now = performance.now();
        if (now - lastUiSyncRef.current >= UI_SYNC_INTERVAL_MS) {
          lastUiSyncRef.current = now;
          setCurrentTime((prev) => (Math.abs(prev - t) > TIME_EPSILON ? t : prev));
        }
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [analysis, getPracticeTimelineAudio, isPlaying, syncTimelineAtTime]);

  const handleStemMuteToggle = useCallback((key: StemKey) => {
    if (!isPro) {
      toast.error("Mute de stems es una función Pro");
      return;
    }
    setStemMuted((prev) => ({ ...prev, [key]: !prev[key] }));
  }, [isPro]);

  const jumpToChord = useCallback((index: number) => {
    if (!chords[index]) return;
    seekTo(chords[index].start || chords[index].time || 0);
  }, [chords, seekTo]);

  const activeChord = chordSync.activeChord;
  const displayedChord = activeChord ? activeChord.chord : "No chord";
  const displayedNextChord = chordSync.nextChord ? chordSync.nextChord.chord : null;
  const timeToNextChord = chordSync.timeToNextChord;
  const learningActiveNotes = useMemo(() => {
    const chordNotes = getChordNotes(displayedChord);
    if (learningInstrument === "bass") {
      return activeChord?.bass_note ? [activeChord.bass_note] : chordNotes.slice(0, 1);
    }
    return chordNotes;
  }, [activeChord?.bass_note, displayedChord, learningInstrument]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-gray-400">Análisis no encontrado</p>
      </div>
    );
  }

  const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const nextChordTime = Number.isFinite(timeToNextChord) ? timeToNextChord?.toFixed(1) : null;

  return (
    <div className="min-h-screen bg-background">
      {hasAudio && (
        <audio
          ref={mainAudioRef}
          src={resolveMediaUrl(playbackAudioUrl)}
          preload="metadata"
          onError={() => {
            console.warn("[AnalysisPage] Playback audio failed", {
              audio_file_ext: analysis.audio_file_ext,
            });
            toast.error("El audio no es reproducible en este navegador. Intenta recargar cuando el preview termine.");
          }}
        />
      )}
      {stemsReady &&
        STEM_KEYS.map((key) => (
          <audio
            key={key}
            ref={(el) => {
              stemRefs.current[key] = el;
            }}
            src={resolveMediaUrl(stemsTask?.stems?.[key])}
            preload="none"
          />
        ))}

      <header className="sticky top-0 z-50 border-b border-white/5 bg-ink-raised">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 md:px-6">
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <Link to="/dashboard" className="grid min-h-11 min-w-11 place-items-center text-gray-400 transition-colors hover:text-white" aria-label="Volver al dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="max-w-full truncate text-base font-semibold text-white sm:max-w-[32rem]">{analysis.filename}</h1>
              <p className="mt-1 text-xs text-tune">Analisis musical listo</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {currentSectionName && (
                  <Badge variant="outline" className="text-xs bg-primary/10 border-primary/30 text-primary">
                    {currentSectionName}
                  </Badge>
                )}
                <Badge variant="outline" className={`${isPro ? "text-brass border-brass/40" : "text-muted-foreground border-border"} text-xs`}>
                  <Crown className="w-3 h-3 mr-1 inline" />
                  {isPro ? "Pro" : "Free"}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Button asChild variant="outline" size="sm" className="min-h-10 flex-1 border-white/10 text-gray-300 gap-2 sm:flex-none">
              <Link to={`/stem-mixer/${id}${stemsReady ? `?task=${encodeURIComponent(stemsTask.id)}` : ""}`}>
                <SlidersHorizontal className="w-4 h-4" />
                Studio
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled
              title="MIDI export aun no esta implementado en este MVP"
              className="border-white/10 text-gray-500 hidden md:flex gap-2 opacity-60 cursor-not-allowed"
            >
              <FileAudio className="w-4 h-4" />
              MIDI soon
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled
              title="PDF de analisis aun no esta implementado; PDF activo vive en setlists/song enrichment"
              className="border-white/10 text-gray-500 hidden md:flex gap-2 opacity-60 cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              PDF soon
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-4 md:px-6 md:py-8">
        <Card className="border-white/10 bg-panel steel-studio-card">
          <CardContent className="space-y-5 p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-5">
              <Badge className="bg-primary/20 text-primary border border-primary/30 justify-center">{analysis.key}</Badge>
              <Badge className="bg-secondary/20 text-secondary border border-secondary/30 justify-center">{analysis.bpm} BPM</Badge>
              <Badge className="bg-white/10 text-white border border-white/10 justify-center">{analysis.time_signature}</Badge>
              <Badge className="bg-white/10 text-white border border-white/10 justify-center">{formatTime(analysis.duration)}</Badge>
              <Badge className={`${stemsReady ? "bg-tune/20 text-tune border-tune/30" : "bg-panel text-muted-foreground border-border"} justify-center`}>
                {stemStatusLabel(stemsTask)}
              </Badge>
            </div>

            <div className="text-center">
              <p className="mb-2 text-xs uppercase tracking-wider text-signal/70">Acorde actual</p>
              <div
                className={`steel-hero-chord inline-flex min-h-[6.25rem] min-w-[10.5rem] items-center justify-center rounded-[1.5rem] px-6 py-4 transition-all duration-200 ${
                  activeChord
                    ? CHORD_COLORS[getChordType(displayedChord || "")]
                    : "text-gray-300"
                }`}
              >
                <span className="font-['JetBrains_Mono'] text-5xl font-bold md:text-6xl">
                  {activeChord ? displayedChord : "--"}
                </span>
              </div>
              {isPro && activeChord && nextChordTime && displayedNextChord && (
                <p className="text-sm text-gray-400 mt-2">
                  Siguiente: <span className="text-white">{displayedNextChord}</span> en{" "}
                  <span className="text-primary">{nextChordTime}s</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <p className="text-xs uppercase tracking-[0.2em] text-signal/60">
                  Aprendizaje desde la primera vista
                </p>
                <Badge className="border border-signal/25 bg-signal/10 text-signal">
                  Learning sync
                </Badge>
              </div>
              <ChordLearningPanel
                chord={displayedChord}
                activeNotes={learningActiveNotes}
                nextChord={displayedNextChord}
                timeToNextChord={timeToNextChord}
                currentTime={currentTime}
                chords={chords}
                compact
                showLabels
                instrumentMode={learningInstrument}
                onInstrumentModeChange={handleLearningInstrumentChange}
                confidence={activeChord?.confidence}
              />
            </div>

            <div
              ref={progressRef}
              className="relative h-4 cursor-pointer rounded-full bg-white/5 shadow-inner touch-none focus:outline-none focus:ring-2 focus:ring-primary/70"
              role="slider"
              aria-label="Buscar en la cancion"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration || 0)}
              aria-valuenow={Math.round(currentTime || 0)}
              tabIndex={0}
              onPointerDown={handleProgressPointerDown}
              onPointerMove={handleProgressPointerMove}
              onPointerUp={handleProgressPointerUp}
              onPointerCancel={handleProgressPointerCancel}
              onKeyDown={handleProgressKeyDown}
            >
              {sections.map((section, i) => (
                <div key={i} className="absolute top-0 h-full border-l border-white/20" style={{ left: `${duration ? (section.start / duration) * 100 : 0}%` }} />
              ))}
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-accent rounded-full" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="flex justify-between text-xs font-mono text-gray-500 sm:text-sm">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>

            <div className="flex items-center justify-center gap-3">
              <Button variant="ghost" size="icon" onClick={restart} disabled={!hasAudio}>
                <Repeat className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={skipBack} disabled={!hasAudio}>
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button size="icon" onClick={togglePlay} className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90" disabled={!hasAudio}>
                {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={skipForward} disabled={!hasAudio}>
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 md:gap-4">
              <div className="space-y-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                <p className="text-sm text-white font-medium">Loop por sección (Free)</p>
                <select
                  className="w-full p-2 bg-black/50 border border-white/10 rounded text-white text-sm"
                  value={loopSectionIndex === null ? "" : String(loopSectionIndex)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLoopSectionIndex(value === "" ? null : Number(value));
                  }}
                >
                  <option value="">Sin sección</option>
                  {sections.map((section, index) => (
                    <option key={`${section.name}-${index}`} value={index}>
                      {section.name} ({formatTime(section.start)} - {formatTime(section.end)})
                    </option>
                  ))}
                </select>
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    className={`border-white/10 ${loopEnabled ? "text-primary" : "text-gray-300"}`}
                    onClick={() => setLoopEnabled((prev) => !prev)}
                    disabled={loopSectionIndex === null}
                  >
                    {loopEnabled ? "Loop activo" : "Activar loop"}
                  </Button>
                  {loopSection && <span className="text-xs text-gray-400">{loopSection.name}</span>}
                </div>
              </div>

              <div className="space-y-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                <p className="text-sm text-white font-medium">Velocidad (Pro)</p>
                {!isPro && <p className="text-xs text-brass">Disponible en plan Pro (0.5x - 1.5x).</p>}
                <Slider
                  value={[isPro ? playbackRate : 1]}
                  onValueChange={(value) => setPlaybackRate(Number(value[0].toFixed(2)))}
                  min={0.5}
                  max={1.5}
                  step={0.05}
                  disabled={!isPro}
                />
                <p className="text-xs text-gray-400">Velocidad actual: {isPro ? `${playbackRate.toFixed(2)}x` : "1.00x (Free)"}</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
              <Button variant="ghost" size="icon" onClick={() => setIsMuted((prev) => !prev)} disabled={!hasAudio}>
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <Slider
                value={[isMuted ? 0 : volume]}
                onValueChange={(v) => {
                  setVolume(v[0]);
                  setIsMuted(false);
                }}
                max={100}
                step={1}
                className="w-32"
                disabled={!hasAudio}
              />
            </div>
          </CardContent>
        </Card>

        <GlobalSongMatchPanel analysis={analysis} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full overflow-x-auto border border-white/10 bg-panel p-1 sm:w-auto">
            <TabsTrigger value="practice" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Practice Mode
            </TabsTrigger>
            <TabsTrigger value="stems" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Stems
            </TabsTrigger>
            <TabsTrigger value="assist" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Asistente
            </TabsTrigger>
          </TabsList>

          <TabsContent value="practice">
            <Card className="border-border bg-panel steel-studio-card">
              <CardHeader>
                <CardTitle className="flex flex-col gap-2 text-foreground sm:flex-row sm:items-center sm:justify-between">
                  <span>Practice Mode MVP</span>
                  {!isPro && (
                    <Badge className="bg-brass/20 text-brass border border-brass/30">
                      Free: loop básico
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-white font-medium">Modo práctica con stems (Pro)</p>
                    <Button
                      variant="outline"
                      className="border-white/10 text-gray-300"
                      onClick={() => {
                        if (!isPro) {
                          toast.error("Mute de stems es una funcionalidad Pro");
                          return;
                        }
                        if (!stemsReady) {
                          toast.info("Las pistas se estan preparando automaticamente");
                          return;
                        }
                        setUseStemPractice((prev) => !prev);
                      }}
                    >
                      {useStemPractice ? "Usando stems" : "Usar stems"}
                    </Button>
                  </div>

                  {!stemsReady && <p className="text-xs text-gray-500">{stemStatusLabel(stemsTask)}</p>}
                  {stemsReady && !isPro && <p className="text-xs text-brass">Mute por instrumento disponible solo en Pro.</p>}

                  {stemsReady && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {STEM_KEYS.map((key) => (
                        <Button
                          key={key}
                          variant="outline"
                          className={`min-h-11 border-white/10 text-sm ${stemMuted[key] ? "text-red-300" : "text-gray-200"}`}
                          onClick={() => handleStemMuteToggle(key)}
                          disabled={!isPro || !useStemPractice}
                        >
                          {stemMuted[key] ? `Unmute ${key}` : `Mute ${key}`}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                  <p className="text-sm text-white font-medium mb-2">Acordes sincronizados</p>
                  <ChordsStrip
                    chords={chords}
                    currentChordIndex={currentChordIndex}
                    isPro={isPro}
                    onJump={jumpToChord}
                  />
                  {!isPro && (
                    <p className="text-xs text-brass mt-2">
                      Free muestra sincronización básica. Pro habilita navegación avanzada por acorde.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="stems">
            <StemQuickMixer ref={quickMixerRef} task={stemsTask} analysisId={id} duration={analysis.duration}
              getAuthHeaders={getAuthHeaders} onRetry={retryStems} retrying={retryingStems} statusError={stemsStatusError}
              onBeforePlay={() => { getAllActiveAudios().forEach(audio => audio.pause()); setIsPlaying(false); }} />
          </TabsContent>

          <TabsContent value="assist">
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Asistente Musical</h3>
                      <p className="text-sm text-gray-400">Sugerencias armónicas y de práctica contextual.</p>
                    </div>
                  </div>
                  <Link to={`/assistant?analysisId=${id}`}>
                    <Button className="bg-primary hover:bg-primary/90 gap-2">Abrir Asistente</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalysisPage;
